"use client";

import { useEffect } from "react";
import { isSettingsHubPath, pathWithoutLocale } from "@/lib/localePath";
import { useNavigationPending } from "./NavigationPendingContext";

/**
 * Routes not wrapped by `(main)` layout (`app/[locale]/(main)/…`).
 * Showing the main-column pending overlay for these never matches `pathWithoutLocale`
 * after navigation (we leave the shell), so the overlay would stick forever.
 */
function isOutsideMainShell(pathWithoutLocale: string): boolean {
  const p = pathWithoutLocale || "/";
  if (p === "/apply-job" || p.startsWith("/apply-job/")) return true;
  if (p === "/auth" || p.startsWith("/auth/")) return true;
  return false;
}

/**
 * Any internal `<a href>` or router.push/replace → instant navigation target + skeleton.
 * Lives inside NavigationPendingProvider (main app shell).
 */
export default function UniversalNavigationIntent() {
  const { setPendingFromLink } = useNavigationPending();

  useEffect(() => {
    const applyDestination = (href: string) => {
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;

        const cur = new URL(window.location.href);
        if (url.pathname === cur.pathname && url.search === cur.search) {
          return;
        }

        const pathOnly = pathWithoutLocale(url.pathname);
        if (isOutsideMainShell(pathOnly)) return;

        const currentPath = pathWithoutLocale(window.location.pathname);
        if (isSettingsHubPath(pathOnly) && isSettingsHubPath(currentPath)) {
          return;
        }

        setPendingFromLink(pathOnly, true);
      } catch {
        /* ignore */
      }
    };

    /** Link clicks: only via `routeChangeStart` (bubble phase in NavigationEvents, after preventDefault). No pointerdown — that ran before tap-vs-drag could cancel. */

    const onRouteChangeStart = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (!detail || typeof detail !== "string") return;
      applyDestination(detail);
    };

    document.addEventListener(
      "routeChangeStart",
      onRouteChangeStart as EventListener,
    );
    return () => {
      document.removeEventListener(
        "routeChangeStart",
        onRouteChangeStart as EventListener,
      );
    };
  }, [setPendingFromLink]);

  return null;
}
